import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { namingConfig } from '@gap/utils/config/naming-config'
import Button from '@mui/material/Button'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import GroupOutlined from '@mui/icons-material/GroupOutlined'
import useStore from 'reusable/mobx-store-utils'
import useInitialMount from 'reusable/hooks/useInitialMount'
import FlexAlignRight from 'reusable/mui/utilities/FlexAlignRight'
import { useOrgGuardStore } from 'features/main/main-router/org-guard/OrgGuardStore'
import { useWebSocketStore } from 'websockets/WebSocketStore'
import AdvisorIntegrationCard from '../AdvisorIntegrationCard'
import ClientSyncAction from '../ClientSyncAction'
import { IntegrationCapabilityRowProps } from '../IntegrationCapabilityRow'
import AdvisorIntegrationFYIStore, { AdvisorIntegrationFYISource, AdvisorIntegrationFYIStoreProvider, useAdvisorIntegrationFYIStore } from './AdvisorIntegrationFYIStore'
import AdvisorIntegrationFYIConnectDialog from './AdvisorIntegrationFYIConnectDialog'
import AdvisorIntegrationFYIManageDialog from './AdvisorIntegrationFYIManageDialog'

interface AdvisorIntegrationFYIProps {
   /** Where the card lives: the create client modal shows the client source in the main row, the integrations page in its rows. */
   source?: Exclude<AdvisorIntegrationFYISource, 'share-dialog'>
}

export default observer(function AdvisorIntegrationFYI(props: AdvisorIntegrationFYIProps) {
   const orgGuardStore = useOrgGuardStore()
   const webSocketStore = useWebSocketStore()
   const advisorIntegrationFYIStore = useStore(() => new AdvisorIntegrationFYIStore({ source: props.source ?? 'integrations', orgGuardStore, webSocketStore }))

   useInitialMount(() => {
      advisorIntegrationFYIStore.fetch()
   })

   useEffect(() => {
      return () => advisorIntegrationFYIStore.dispose()
   }, [advisorIntegrationFYIStore])

   const clientSource = advisorIntegrationFYIStore.clientSource

   return <AdvisorIntegrationFYIStoreProvider store={advisorIntegrationFYIStore}>

      <AdvisorIntegrationFYIConnectDialog />
      <AdvisorIntegrationFYIManageDialog />

      <AdvisorIntegrationCard
         title="FYI"
         loading={advisorIntegrationFYIStore.loader.isLoading}
         subTitle={advisorIntegrationFYIStore.cardSubTitle}
         status={advisorIntegrationFYIStore.status}
         onClick={advisorIntegrationFYIStore.status !== 'connected' ? advisorIntegrationFYIStore.openConnectDialog : advisorIntegrationFYIStore.disconnect}
         icon={<img src="/images/icons/fyi-docs-logo.svg" width="35" />}
         clientSource={props.source === 'create-client-modal' ? clientSource : undefined}
         rows={advisorIntegrationFYIStore.showClientSourceRow ? capabilityRows(advisorIntegrationFYIStore) : undefined}
         footer={advisorIntegrationFYIStore.showManageAction ? <ManageSyncFooter /> : undefined}
      />
   </AdvisorIntegrationFYIStoreProvider>
})

/** The Documents and Clients rows for the current connection and client source state. */
function capabilityRows(advisorIntegrationFYIStore: AdvisorIntegrationFYIStore): IntegrationCapabilityRowProps[] {
   const hasKeys = advisorIntegrationFYIStore.status !== 'not connected'
   const syncsClients = advisorIntegrationFYIStore.status === 'connected' && advisorIntegrationFYIStore.isClientSource

   return [
      {
         id: 'documents',
         icon: <DescriptionOutlined />,
         label: 'Documents',
         description: hasKeys ? 'Stored in FYI' : `Store documents from ${namingConfig.product.singular} in FYI`,
      },
      {
         id: 'clients',
         icon: <GroupOutlined />,
         label: 'Clients',
         description: advisorIntegrationFYIStore.isClientSource ? 'Client source' : 'Use FYI as your client source',
         badge: advisorIntegrationFYIStore.isClientSource ? undefined : { label: 'FYI Elite', colorVariant: 'accent' },
         action: syncsClients ? <ClientSyncAction state={advisorIntegrationFYIStore.clientsRowState} onSyncNow={advisorIntegrationFYIStore.syncClientsNow} /> : undefined,
      },
   ]
}

const ManageSyncFooter = observer(function ManageSyncFooter() {
   const advisorIntegrationFYIStore = useAdvisorIntegrationFYIStore()

   return <FlexAlignRight>
      <Button variant="text" size="small" onClick={advisorIntegrationFYIStore.openManageDialog}>Change what syncs</Button>
   </FlexAlignRight>
})
